/**
 * Onboarding Backend Adapter — IN-3.a-lite
 * ────────────────────────────────────────
 * Thin persistence seam between the in-memory pure writers (sessions,
 * inviteTokens, documents, …) and the durable store (localStorage today,
 * Supabase tomorrow).
 *
 * Hard rules:
 *   • Pure writers stay pure — they NEVER call the backend directly.
 *   • Backend abstracts ONLY load/save of the full snapshot. No business
 *     logic. No security checks. No fake auth.
 *   • Snapshot shape is versioned (`v: 1`). A SupabaseStore that returns
 *     a different version must throw rather than silently coerce.
 */

import type { ComplianceDocument } from "@/data/contractor/compliance/complianceDocuments";
import type {
  OnboardingAuditEvent,
  OnboardingInviteToken,
  OnboardingSession,
} from "../types";

export const ONBOARDING_SNAPSHOT_VERSION = 1 as const;

/**
 * Sprint A.2 (Lock #7) — per-write monotonic counter. Multi-tab handlers
 * compare `seq` before replacing local state. Stale writes are rejected,
 * not silently overwritten.
 */
export interface OnboardingSnapshot {
  v: typeof ONBOARDING_SNAPSHOT_VERSION;
  /** Monotonic write counter. Newer wins; equal/older is rejected. */
  seq: number;
  inviteTokens: OnboardingInviteToken[];
  sessions: OnboardingSession[];
  auditEvents: OnboardingAuditEvent[];
  complianceDocuments: ComplianceDocument[];
}

export function emptyOnboardingSnapshot(): OnboardingSnapshot {
  return {
    v: ONBOARDING_SNAPSHOT_VERSION,
    seq: 0,
    inviteTokens: [],
    sessions: [],
    auditEvents: [],
    complianceDocuments: [],
  };
}

export interface OnboardingBackend {
  /** Human-readable id for diagnostics + tests. */
  readonly kind: "local" | "supabase";
  /** Load the persisted snapshot. Must never throw — return empty on miss/corrupt. */
  loadSnapshot(): OnboardingSnapshot;
  /** Persist the full snapshot. Synchronous for local; future Supabase impl wraps async. */
  saveSnapshot(snapshot: OnboardingSnapshot): void;
}
